import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { Link } from 'react-router-dom';
import { Modal } from '../modal';
import { Colors } from '../../styles/colors';

const Title = styled.h2`
  ${tw`text-2xl font-bold mb-4 text-text-primary`}
`;

const Body = styled.p`
  ${tw`text-text-secondary leading-relaxed mb-6`}
`;

const SiteHighlight = styled.span`
  ${tw`font-semibold text-text-primary`}
`;

const Actions = styled.div`
  ${tw`flex flex-col sm:flex-row gap-3 justify-end`}
`;

const Button = styled.button<{ $variant?: 'primary' | 'secondary' }>`
  ${tw`px-5 py-3 rounded-lg font-semibold transition-colors`}
  ${({ $variant }) =>
    $variant === 'secondary'
      ? tw`bg-white border border-border-default text-text-secondary hover:bg-bg-secondary`
      : css`
          background-color: ${Colors.Black};
          color: white;
          &:hover {
            background-color: ${Colors.BlackHover};
          }
          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        `}
`;

const CheckboxContainer = styled.label`
  ${tw`flex items-start gap-3 mb-6 cursor-pointer`}
`;

const Checkbox = styled.input`
  ${tw`mt-1`}
`;

const CheckboxText = styled.span`
  ${tw`text-sm text-text-secondary leading-relaxed`}
`;

const PolicyLink = styled(Link)`
  ${tw`text-black underline`}
`;

const ErrorText = styled.p`
  ${tw`text-sm text-red-600 mb-4`}
`;

const OwnerSiteNotice = styled.p`
  ${tw`text-sm text-black leading-relaxed mb-2 md:mb-6`}
`;

const OwnerSiteNoticeHost = styled.span`
  ${tw`font-semibold`}
`;

type OwnerSiteRedirectNoticeProps = {
  ownerSiteHost: string;
  className?: string;
};

/** Shown below Continue when checkout moves to another brand's site (D40). */
export function OwnerSiteRedirectNotice({
  ownerSiteHost,
  className,
}: OwnerSiteRedirectNoticeProps) {
  return (
    <OwnerSiteNotice className={className} data-testid="referral-owner-site-notice">
      This car is rented through <OwnerSiteNoticeHost>{ownerSiteHost}</OwnerSiteNoticeHost>. When
      you continue, checkout moves to <OwnerSiteNoticeHost>{ownerSiteHost}</OwnerSiteNoticeHost>{' '}
      with the same account and dates. Payment, messages and trip updates for this trip will be
      there.
    </OwnerSiteNotice>
  );
}

const AirportNotice = styled.p`
  ${tw`text-sm text-red-700 leading-relaxed mb-2 md:mb-6`}
`;

/** D40: no single site offers both chosen airports. */
export function AirportNotBookableNotice({ message }: { message: string }) {
  return <AirportNotice data-testid="airport-not-bookable-notice">{message}</AirportNotice>;
}

type ReferralOwnerTermsModalProps = {
  isOpen: boolean;
  ownerSiteHost: string;
  onContinue: () => void;
  isLoading?: boolean;
};

export function ReferralOwnerTermsModal({
  isOpen,
  ownerSiteHost,
  onContinue,
  isLoading = false,
}: ReferralOwnerTermsModalProps) {
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [showError, setShowError] = useState(false);

  const handleContinue = () => {
    if (!acceptedTerms) {
      setShowError(true);
      return;
    }
    onContinue();
  };

  return (
    <Modal isOpen={isOpen} onClose={() => undefined} modalWidth="min(560px, calc(100vw - 2rem))">
      <Title>Welcome to {ownerSiteHost}</Title>
      <Body>
        Please accept this site&apos;s terms of service and privacy policy before continuing to
        checkout.
      </Body>

      <CheckboxContainer>
        <Checkbox
          type="checkbox"
          checked={acceptedTerms}
          onChange={event => {
            setAcceptedTerms(event.target.checked);
            if (event.target.checked) {
              setShowError(false);
            }
          }}
          data-testid="referral-owner-terms-agreement"
        />
        <CheckboxText>
          I agree to the{' '}
          <PolicyLink to="/policies/terms" target="_blank" rel="noopener noreferrer">
            terms of service
          </PolicyLink>{' '}
          and{' '}
          <PolicyLink to="/policies/privacy" target="_blank" rel="noopener noreferrer">
            privacy policy
          </PolicyLink>{' '}
          for <SiteHighlight>{ownerSiteHost}</SiteHighlight>.
        </CheckboxText>
      </CheckboxContainer>

      {showError && (
        <ErrorText>
          Please read and accept the terms and privacy policy before continuing.
        </ErrorText>
      )}

      <Actions>
        <Button type="button" onClick={handleContinue} disabled={isLoading}>
          {isLoading ? 'Continuing...' : 'Continue to checkout'}
        </Button>
      </Actions>
    </Modal>
  );
}
